import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHelmet from '@/components/base/SEOHelmet';
import { getMangaBySlug, mangaList, mangaCategories } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = getMangaBySlug(slug || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">
              漫画未找到
            </h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedManga = mangaList
    .filter((m) => m.slug !== manga.slug && m.categories.some((c) => manga.categories.includes(c)))
    .slice(0, 6);

  return (
    <>
      <SEOHelmet
        title={`${manga.title} - 番木瓜漫画免费全集 | 番木瓜漫画${manga.title}免费阅读下拉式`}
        description={`番木瓜漫画提供${manga.title}免费全集在线阅读，下拉式畅快阅读体验。${manga.description.slice(0, 120)}`}
        keywords={`番木瓜漫画,${manga.title},免费全集,番木瓜漫画免费,番木瓜漫画网,下拉式漫画,${manga.categories.join(',')}漫画`}
        canonicalUrl={`https://www.fanmugua.com/manga/${manga.slug}`}
        ogType="article"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${manga.title} - 番木瓜漫画免费全集`,
          description: manga.description,
          author: {
            '@type': 'Person',
            name: manga.author,
          },
          datePublished: manga.chapters_list[0]?.updatedAt || '',
          dateModified: manga.updatedAt,
          publisher: {
            '@type': 'Organization',
            name: '番木瓜漫画',
          },
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen bg-background-50 pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/rexue" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              漫画分类
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            {manga.categories[0] && (
              <>
                <Link
                  to={`/category/${mangaCategories.find((mc) => mc.name === manga.categories[0])?.slug || ''}`}
                  className="hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  {manga.categories[0]}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </>
            )}
            <span className="text-foreground-900 font-medium">{manga.title}</span>
          </nav>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
          {/* Manga Header */}
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="flex-shrink-0 w-full sm:w-[220px] md:w-[260px]">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={manga.cover}
                  alt={`${manga.title} - 番木瓜漫画免费全集在线阅读`}
                  title={`${manga.title} - 番木瓜漫画免费阅读下拉式`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">作者：{manga.author}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  manga.status === '已完结'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {manga.status}
                </span>
                <span className="flex items-center gap-0.5 text-sm text-foreground-600">
                  <i className="ri-star-fill text-primary-500 text-xs"></i>
                  {manga.rating} 分
                </span>
                <span className="text-sm text-foreground-500">
                  {(manga.views / 10000).toFixed(0)} 万人气
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-md bg-background-100 text-foreground-600 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {manga.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="#"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-play-circle-line text-base"></i>
                  开始阅读 第1话
                </Link>
                <Link
                  to={`/category/${mangaCategories.find((mc) => mc.name === manga.categories[0])?.slug || ''}`}
                  className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-all duration-200 whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-grid-line"></i>
                  更多{manga.categories[0]}漫画
                </Link>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-bold text-lg text-foreground-900">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共{manga.chapters}话
                </span>
              </h2>
              <div className="flex bg-background-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    viewMode === 'grid'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500'
                  }`}
                >
                  <i className="ri-layout-grid-line mr-1"></i>网格
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    viewMode === 'list'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500'
                  }`}
                >
                  <i className="ri-list-check mr-1"></i>列表
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {generateChapters(manga.chapters).map((ch) => (
                  <Link
                    key={ch}
                    to="#"
                    className="flex items-center justify-center px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/30 transition-all duration-200 whitespace-nowrap"
                  >
                    第{ch}话
                  </Link>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {generateChapters(manga.chapters).map((ch) => (
                  <Link
                    key={ch}
                    to="#"
                    className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/30 transition-all duration-200"
                  >
                    <span>第{ch}话 {ch === 1 ? manga.chapters_list[0]?.title || '' : ch === manga.chapters ? manga.chapters_list[manga.chapters_list.length - 1]?.title || '' : ''}</span>
                    <span className="text-xs text-foreground-400">
                      {ch === 1 && manga.chapters_list[0] ? manga.chapters_list[0].updatedAt : manga.updatedAt}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Related Manga */}
          {relatedManga.length > 0 && (
            <section>
              <h2 className="font-heading font-bold text-lg text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.slug}
                    to={`/manga/${rm.slug}`}
                    className="group block"
                    data-product-shop
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2.5">
                      <img
                        src={rm.cover}
                        alt={`${rm.title} - 番木瓜漫画`}
                        title={`${rm.title} - 番木瓜漫画免费全集`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="font-heading font-medium text-xs text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rm.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}

function generateChapters(total: number): number[] {
  const chapters: number[] = [];
  for (let i = 1; i <= total; i++) {
    chapters.push(i);
  }
  return chapters;
}
